import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
// @mui
import {
  Card,
  Table,
  Stack,
  Button,
  Alert,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  TableHead,
  CircularProgress,
  Box,
  Popover,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
} from "@mui/material";
// components

import Iconify from "../components/iconify";
import Scrollbar from "../components/scrollbar";

import { useNavigate, useSearchParams } from "react-router-dom";
import PickemLeaderboardTable from "src/components/pickem-edit/PickemLeaderboardTable";
import { useLeaderboard } from "src/hooks/api/useLeaderboard";
import { usePickems } from "src/hooks/api/usePickems";
import { get, post } from "src/utils/networkUtils";
// ----------------------------------------------------------------------

export default function LeaderboardPage() {
  const navigate = useNavigate();
  const { fetchPickemLeaderboard, fetchLeagueLeaderboard } = useLeaderboard();
  const { fetchPickemById } = usePickems();
  
  const [loading, setLoading] = useState(false);
  const [leaderboard, setLeaderboard] = useState(null);
  const [pickemStats, setPickemStats] = useState(null);
  const [pickemId, setPickemId] = useState(null);
  const [tournamentId, setTournamentId] = useState(null);
  const [leagueId, setLeagueId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [pickem, setPickem] = useState(null)
  const [open, setOpen] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [checkedUsers, setCheckedUsers] = useState([]);
  const [openSendEmail, setOpenSendEmail] = useState(false);
  const [sendEmailText, setSendEmailText] = useState({
    prize_text_en: "",
    prize_text_lv: "",
    prize_text_et: "",
    prize_text_ru: "",
    prize_text_sk: "",
  });

  useEffect(()=>{
      if(pickem && leaderboard && leaderboard.leaderboard){
        const answers_leaderboard= leaderboard.leaderboard.map((userLeaderboard)=>{
          const answers = userLeaderboard.selections.reduce(
            (accum, selection)=> {
              const question = pickem.questions.find((item)=>item.question_id===selection.question_id)
              if(question){
                if(question.is_tiebreak){
                  accum.custom_value = +question.tiebreak_score === +selection.custom_value
                }
                const option = question.options.find((item)=>item.option_id===selection.option_id)
                if(option){
                  if (!!option.is_correct) {
                    accum.correct++
                  }
                  if (!option.is_correct) {
                    accum.incorrect++
                  }
                }
              }
              return accum
            }, {correct: 0, incorrect: 0, custom_value:false})
          return {...userLeaderboard, answers}
        })
        setLeaderboard({...leaderboard, leaderboard:answers_leaderboard})
      }
  },[pickem, leaderboard])

  useEffect(() => {
    const loadLeaderboardData = async () => {
      if (searchParams.has("pickem_id")) {
        const pickemId = searchParams.get("pickem_id");
        setPickemId(pickemId);
        
        const fetchSnapshot = async () => {
          try {
            const stats = await fetchPickemLeaderboard(pickemId, 0, 1);
            setPickemStats(stats);
            
            const pickem = await fetchPickemById(pickemId);
            setPickem(pickem);
            setLoading(false);
          } catch (error) {
            console.error("Failed to fetch leaderboard:", error);
          }
        };

        fetchSnapshot();

        // Poll every 60s to mimic old auto-update behavior
        const intervalId = setInterval(() => {
          fetchSnapshot();
        }, 60000);

        return () => clearInterval(intervalId);

      } else if (
        searchParams.has("league_id") &&
        searchParams.has("tournament_id")
      ) {
        const leagueId = searchParams.get("league_id");
        const tournamentId = searchParams.get("tournament_id");
        setTournamentId(tournamentId);
        setLeagueId(leagueId);
        
        try {
          const response = await get(
            `/admin/league-tournament-leaderboard/${leagueId}?tournament_id=${tournamentId}&offset=0&limit=1000`
          );
          setLeaderboard(response);
          setLoading(false);
        } catch (error) {
          console.error("Failed to fetch league leaderboard:", error);
        }
      }
    };
    
    loadLeaderboardData();
  }, [searchParams, fetchPickemLeaderboard, fetchPickemById]);

  const handleForcePickemScoring = async () => {
    setLoading(true);
    try {
      await post(`/admin/pickems/${pickemId}/run-leaderboard-scoring`);
      window.location.reload(false);
    } catch (error) {
      console.error("Failed to force scoring:", error);
      setLoading(false);
    }
  };

  const handleAddTrophy = async (userId, trophy) => {
    setLoading(true);
    const params =
      pickemId !== null
        ? `pickem_id=${pickemId}`
        : `tournament_id=${tournamentId}`;
    try {
      await post(
        `/admin/leaderboard/user/add-trophy?${params}`,
        { user_id: userId, trophy }
      );
      window.location.reload(false);
    } catch (error) {
      console.error("Failed to add trophy:", error);
      setLoading(false);
    }
  };

  const handleRemoveTrophy = async (userId) => {
    setLoading(true);
    const params =
      pickemId !== null
        ? `pickem_id=${pickemId}`
        : `tournament_id=${tournamentId}`;
    try {
      await post(
        `/admin/leaderboard/user/remove-trophy?${params}`,
        { user_id: userId }
      );
      window.location.reload(false);
    } catch (error) {
      console.error("Failed to remove trophy:", error);
      setLoading(false);
    }
  };

  const handleForceTournamentsScoring = async () => {
    setLoading(true);
    try {
      await post(`/admin/pickems/${pickemId}/run-tournaments-leaderboard-scoring`);
      window.location.reload(false);
    } catch (error) {
      console.error("Failed to force tournaments scoring:", error);
      setLoading(false);
    }
  };

  const handleDownloadCsv = async () => {
    setLoading(true);
    try {
      const response = await get(`/admin/leaderboard/export-csv/${pickemId}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      const now = new Date();
      const dateTime = now.toISOString().slice(0, 16).replace('T', ' ');
      link.setAttribute('download', `leaderboard-pickem-${pickemId} ${dateTime}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setLoading(false);
    } catch (error) {
      console.error('Error during CSV download', error);
      setLoading(false);
    }
  };

  const handleOpenSendEmailDialog = () => {
    setSendEmailText({
      prize_text_en: "",
      prize_text_lv: "",
      prize_text_et: "",
      prize_text_ru: "",
      prize_text_sk: "",

      contestname_en: "",
      contestname_ru: "",
      contestname_lv: "",
      contestname_et: "",
      contestname_sk: "",
    });
    setOpenSendEmail(true);
  };

  const handleSendEmail = async () => {
    setLoading(true);
    setOpenSendEmail(false);
    try {
      await post('/admin/send-prize-notifications', {
        ...sendEmailText,
        user_ids: checkedUsers,
        pickem_id: pickemId,
        league_id: leagueId,
        tournament_id: tournamentId,
      });
      window.location.reload(false);
    } catch (error) {
      console.error("Failed to send email:", error);
      setLoading(false);
    }
  };

  const handleCloseMenu = () => {
    setOpen(null);
    setOpenId(null);
  };

  const handleChangeEmailFields = (e) => {
    const { id, value } = e.target;

    setSendEmailText((prevState) => ({
      ...prevState,
      [id]: value,
    }));
  };

  const handleSelectAll = () => {
    if (checkedUsers.length === leaderboard.leaderboard.length) {
      setCheckedUsers([]);
    } else {
      setCheckedUsers(leaderboard.leaderboard.map((user) => user.user_id));
    }
  };

  const handleCheckUser = (userId) => {
    if (checkedUsers.includes(userId)) {
      setCheckedUsers(checkedUsers.filter((id) => id !== userId));
    } else {
      setCheckedUsers([...checkedUsers, userId]);
    }
  };

  const handleOpenMenu = (event, userId) => {
    setOpen(event.currentTarget);
    setOpenId(userId);
  };

  console.log('leaderboard', leaderboard);
  console.log('pickemStats', pickemStats);

  return (
    <>
      <Helmet>
        <title>Leaderboard </title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            Leaderboard View
          </Typography>
        </Stack>
        {leagueId !== null &&
          tournamentId !== null &&
          checkedUsers.length > 0 && (
            <Card style={{ padding: 20 }}>
              <Box>
                <Typography variant="h5" component="div" sx={{ marginBottom: 2 }}>
                  Message
              </Typography>
                <Button
                  variant="outlined"
                  startIcon={<Iconify icon="eva:paper-plane-outline" />}
                  onClick={() => handleOpenSendEmailDialog()}
                  sx={{ mr: 2 }}
                >
                  Send Prize Email
                </Button>
              </Box>
            </Card>
          )}
        {pickemId !== null ? (
          <Card style={{ padding: 20 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              mb={5}
            >
              <Typography variant="h5" component="div" sx={{ marginBottom: 2 }}>
                Scoring
              </Typography>
              <Box>
                {checkedUsers.length > 0 && (
                  <Button
                    variant="outlined"
                    startIcon={<Iconify icon="eva:paper-plane-outline" />}
                    onClick={() => handleOpenSendEmailDialog()}
                    sx={{ mr: 2 }}
                  >
                    Send Prize Email
                  </Button>
                )}

                <Button
                  variant="contained"
                  startIcon={<Iconify icon="eva:list-outline" />}
                  onClick={() => handleForceTournamentsScoring()}
                  sx={{ mr: 2 }}
                >
                  Update All Leaderboards
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Iconify icon="eva:rewind-right-outline" />}
                  onClick={() => handleForcePickemScoring()}
                  sx={{ mr: 2 }}
                >
                  Score Daily
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Iconify icon="eva:download-outline" />}
                  onClick={() => handleDownloadCsv()}
                >
                  Export Csv
                </Button>
              </Box>
            </Stack>

            {pickemStats !== null ? (
              <>
                <Alert sx={{ marginTop: 2 }} severity="info">
                  {`Pickem Scoring progress: ${pickemStats.completedScoringCount} / ${pickemStats.selectionsCount}`}
                </Alert>
                <Alert sx={{ marginTop: 2 }} severity="info">
                  {`Tournament Scoring progress: ${pickemStats.completedTournamentsScoringCount} / ${pickemStats.selectionsCount}`}
                </Alert>
              </>
            ) : null}
          </Card>
        ) : null}

        <Card>
          {((pickemId != null && pickemStats === null) || (leagueId != null && tournamentId != null && leaderboard === null) || loading) && (
            <Box
              sx={{ display: "flex", justifyContent: "center" }}
              style={{ padding: 20 }}
            >
              <CircularProgress />
            </Box>
          )}
          {pickemId != null && pickemStats !== null && (<PickemLeaderboardTable pickemId={pickemId} />)}
          {leagueId != null && tournamentId != null && leaderboard != null && !loading && (
            <Scrollbar>
              <TableContainer sx={{ minWidth: 800 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <Checkbox
                          checked={
                            checkedUsers.length === leaderboard.leaderboard.length &&
                            leaderboard.leaderboard.length > 0
                          }
                          onChange={handleSelectAll}
                        />
                      </TableCell>
                      <TableCell>Rank</TableCell>
                      <TableCell>Username</TableCell>
                      <TableCell>Favorite Team</TableCell>
                      <TableCell>Points</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {leaderboard.leaderboard.map((user) => (
                      <TableRow key={user.user_id}>
                        <TableCell>
                          <Checkbox
                            checked={checkedUsers.includes(user.user_id)}
                            onChange={() => handleCheckUser(user.user_id)}
                          />
                        </TableCell>
                        <TableCell>{user.rank}</TableCell>
                        <TableCell>{user.username}</TableCell>
                        <TableCell>
                          {user.favourite_team_logo && (
                            <img
                              src={user.favourite_team_logo}
                              alt="team logo"
                              style={{ width: 24, height: 24 }}
                            />
                          )}
                        </TableCell>
                        <TableCell>{user.points}</TableCell>
                        <TableCell>
                          <IconButton
                            size="large"
                            color="inherit"
                            onClick={(event) => handleOpenMenu(event, user.user_id)}
                          >
                            <Iconify icon={'eva:more-vertical-fill'} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>
          )}
        </Card>
      </Container>
      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            p: 1,
            width: 200,
            "& .MuiMenuItem-root": {
              px: 1,
              typography: "body2",
              borderRadius: 0.75,
            },
          },
        }}
      >
        <MenuItem onClick={() => handleAddTrophy(openId, "gold")}>
          <Iconify icon={"eva:star-outline"} sx={{ mr: 1 }} />
          Add Gold Trophy
        </MenuItem>

        <MenuItem onClick={() => handleAddTrophy(openId, "silver")}>
          <Iconify icon={"eva:star-outline"} sx={{ mr: 1 }} />
          Add Silver Trophy
        </MenuItem>
        <MenuItem onClick={() => handleRemoveTrophy(openId)}>
          <Iconify icon={"eva:close-outline"} sx={{ mr: 1 }} />
          Remove Trophy
        </MenuItem>
        {pickemId !== null ? (
          <MenuItem
            onClick={() =>
              navigate(
                `/dashboard/user-selections?pickem_id=${pickemId}&user_id=${openId}`
              )
            }
          >
            <Iconify icon={"eva:eye-outline"} sx={{ mr: 1 }} />
            User Selections
          </MenuItem>
        ) : null}
      </Popover>
      <Dialog
        open={openSendEmail}
        onClose={() => {}}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth
        maxWidth="md"
      >
        <DialogTitle id="alert-dialog-title">Send Email</DialogTitle>
        <DialogContent>
          <TextField
            id="prize_text_en"
            label="Prize Text EN"
            fullWidth
            value={sendEmailText.prize_text_en}
            variant="standard"
            onChange={handleChangeEmailFields}
          />
          <TextField
            id="prize_text_lv"
            label="Prize Text LV"
            fullWidth
            value={sendEmailText.prize_text_lv}
            variant="standard"
            onChange={handleChangeEmailFields}
          />
          <TextField
            id="prize_text_ru"
            label="Prize Text RU"
            fullWidth
            value={sendEmailText.prize_text_ru}
            variant="standard"
            onChange={handleChangeEmailFields}
          />
          <TextField
            id="prize_text_et"
            label="Prize Text ET"
            fullWidth
            value={sendEmailText.prize_text_et}
            variant="standard"
            onChange={handleChangeEmailFields}
          />
          <TextField
            id="prize_text_sk"
            label="Prize Text SK"
            fullWidth
            value={sendEmailText.prize_text_sk}
            variant="standard"
            onChange={handleChangeEmailFields}
          />
          <TextField
            id="contestname_en"
            label="Contest Name EN"
            fullWidth
            value={sendEmailText.contestname_en}
            variant="standard"
            onChange={handleChangeEmailFields}
          />
          <TextField
            id="contestname_lv"
            label="Contest Name LV"
            fullWidth
            value={sendEmailText.contestname_lv}
            variant="standard"
            onChange={handleChangeEmailFields}
          />
          <TextField
            id="contestname_ru"
            label="Contest Name RU"
            fullWidth
            value={sendEmailText.contestname_ru}
            variant="standard"
            onChange={handleChangeEmailFields}
          />
          <TextField
            id="contestname_et"
            label="Contest Name ET"
            fullWidth
            value={sendEmailText.contestname_et}
            variant="standard"
            onChange={handleChangeEmailFields}
          />
          <TextField
            id="contestname_sk"
            label="Contest Name SK"
            fullWidth
            value={sendEmailText.contestname_sk}
            variant="standard"
            onChange={handleChangeEmailFields}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSendEmail(false)}>Cancel</Button>
          <Button onClick={() => handleSendEmail()} autoFocus>
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}